/**
 * Утилита для обрезки линий, которые пересекаются с прямоугольниками (трубами)
 */

/**
 * Проверяет, находится ли точка внутри прямоугольника
 * @param {number} px - X координата точки
 * @param {number} py - Y координата точки
 * @param {object} rect - Прямоугольник {x, y, width, height}
 * @returns {boolean}
 */
function isPointInsideRect(px, py, rect) {
  return px >= rect.x && 
         px <= rect.x + rect.width && 
         py >= rect.y && 
         py <= rect.y + rect.height;
}

/**
 * Находит точку пересечения линии с границей прямоугольника
 * @param {number} x1, y1 - начало линии
 * @param {number} x2, y2 - конец линии
 * @param {object} rect - прямоугольник {x, y, width, height}
 * @returns {array} - массив точек пересечения [{x, y, t}], где t - параметр от 0 до 1 на линии
 */
function lineRectIntersections(x1, y1, x2, y2, rect) {
  const intersections = [];
  
  // Границы прямоугольника
  const left = rect.x;
  const right = rect.x + rect.width;
  const top = rect.y;
  const bottom = rect.y + rect.height;
  
  // Проверяем пересечение с каждой из 4-х сторон
  const edges = [
    { x1: left, y1: top, x2: right, y2: top },     // верхняя
    { x1: right, y1: top, x2: right, y2: bottom }, // правая
    { x1: right, y1: bottom, x2: left, y2: bottom }, // нижняя
    { x1: left, y1: bottom, x2: left, y2: top },   // левая
  ];
  
  edges.forEach(edge => {
    const intersection = lineLineIntersection(
      x1, y1, x2, y2,
      edge.x1, edge.y1, edge.x2, edge.y2
    );
    
    if (intersection) {
      intersections.push(intersection);
    }
  });
  
  return intersections;
}

/**
 * Находит точку пересечения двух линий
 * @returns {object|null} - {x, y, t} или null, где t - параметр на первой линии (0..1)
 */
function lineLineIntersection(x1, y1, x2, y2, x3, y3, x4, y4) {
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  
  if (Math.abs(denom) < 0.0001) {
    return null; // линии параллельны
  }
  
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
  
  // Проверяем, что пересечение находится на обоих отрезках
  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    return {
      x: x1 + t * (x2 - x1),
      y: y1 + t * (y2 - y1),
      t: t,
    };
  }
  
  return null;
}

/**
 * Обрезает линию, удаляя части, которые находятся внутри прямоугольников
 * @param {array} points - массив координат линии [x1, y1, x2, y2, ...]
 * @param {array} rects - массив прямоугольников {x, y, width, height}
 * @returns {array} - массив сегментов линий [[x1, y1, x2, y2], [...], ...]
 */
export function trimLineAgainstRects(points, rects) {
  if (!points || points.length < 4) {
    return [points];
  }
  
  if (!rects || rects.length === 0) {
    return [points];
  }
  
  // Разбиваем polyline на отдельные сегменты
  const segments = [];
  for (let i = 0; i < points.length - 2; i += 2) {
    segments.push({
      x1: points[i],
      y1: points[i + 1],
      x2: points[i + 2],
      y2: points[i + 3],
    });
  }
  
  const resultSegments = [];
  
  segments.forEach(seg => {
    let currentSegments = [seg];
    
    // Применяем обрезку по каждому прямоугольнику
    rects.forEach(rect => {
      const newSegments = [];
      
      currentSegments.forEach(s => {
        const trimmed = trimSegmentAgainstRect(s.x1, s.y1, s.x2, s.y2, rect);
        newSegments.push(...trimmed);
      });
      
      currentSegments = newSegments;
    });
    
    resultSegments.push(...currentSegments);
  });
  
  // Конвертируем обратно в массивы точек для каждого сегмента
  return resultSegments.map(seg => [seg.x1, seg.y1, seg.x2, seg.y2]);
}

/**
 * Обрезает один сегмент линии относительно одного прямоугольника
 * @returns {array} - массив сегментов [{x1, y1, x2, y2}, ...]
 */
function trimSegmentAgainstRect(x1, y1, x2, y2, rect) {
  const p1Inside = isPointInsideRect(x1, y1, rect);
  const p2Inside = isPointInsideRect(x2, y2, rect);
  
  // Обе точки снаружи - проверяем пересечения
  if (!p1Inside && !p2Inside) {
    const intersections = lineRectIntersections(x1, y1, x2, y2, rect);
    
    if (intersections.length === 0) {
      // Линия не пересекает прямоугольник
      return [{ x1, y1, x2, y2 }];
    } else if (intersections.length === 2) {
      // Линия проходит насквозь - разбиваем на два сегмента
      intersections.sort((a, b) => a.t - b.t);
      const i1 = intersections[0];
      const i2 = intersections[1];
      
      return [
        { x1, y1, x2: i1.x, y2: i1.y },
        { x1: i2.x, y1: i2.y, x2, y2 },
      ];
    } else {
      // Одно пересечение (касание) - оставляем линию как есть
      return [{ x1, y1, x2, y2 }];
    }
  }
  
  // Первая точка внутри, вторая снаружи
  if (p1Inside && !p2Inside) {
    const intersections = lineRectIntersections(x1, y1, x2, y2, rect);
    if (intersections.length > 0) {
      // Берем ближайшее пересечение к p2
      const intersection = intersections.reduce((nearest, curr) => {
        return curr.t > nearest.t ? curr : nearest;
      });
      return [{ x1: intersection.x, y1: intersection.y, x2, y2 }];
    }
    return []; // вся линия внутри
  }
  
  // Первая точка снаружи, вторая внутри
  if (!p1Inside && p2Inside) {
    const intersections = lineRectIntersections(x1, y1, x2, y2, rect);
    if (intersections.length > 0) {
      // Берем ближайшее пересечение к p1
      const intersection = intersections.reduce((nearest, curr) => {
        return curr.t < nearest.t ? curr : nearest;
      });
      return [{ x1, y1, x2: intersection.x, y2: intersection.y }];
    }
    return []; // вся линия внутри
  }
  
  // Обе точки внутри - удаляем линию
  return [];
}

function circleOverlapsRect(cx, cy, radius, rect) {
  const closestX = Math.max(rect.x, Math.min(cx, rect.x + rect.width));
  const closestY = Math.max(rect.y, Math.min(cy, rect.y + rect.height));
  const dx = cx - closestX;
  const dy = cy - closestY;
  return dx * dx + dy * dy <= radius * radius + 1e-4;
}

/** Часть окружности, которая не попала внутрь прямоугольников. Целый круг остаётся кругом. */
function trimCircleAgainstRects(shape, rects) {
  const radius = shape.radius || 0;
  if (radius <= 0) return [shape];
  const hits = (rects || []).filter((rect) => circleOverlapsRect(shape.x, shape.y, radius, rect));
  if (hits.length === 0) return [shape];

  const steps = 48;
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    const angle = (i / steps) * Math.PI * 2;
    points.push(shape.x + radius * Math.cos(angle), shape.y + radius * Math.sin(angle));
  }
  return trimLineAgainstRects(points, hits)
    .filter((segment) => segment && segment.length >= 4)
    .map((segment) => ({
      ...shape,
      type: 'line',
      points: segment,
    }));
}

/**
 * Обрезает все линии shape относительно труб
 * @param {array} shapes - массив shape объектов
 * @param {array} cylinders - массив труб {x, y, width, height}
 * @param {string} layerFilter - название слоя для фильтрации (например, 'FASTENER')
 * @returns {array} - массив shape объектов с обрезанными линиями
 */
export function trimShapesAgainstCylinders(shapes, cylinders, layerFilter = null) {
  if (!shapes || !cylinders || cylinders.length === 0) {
    return shapes;
  }
  
  return shapes.flatMap(shape => {
    // Пропускаем shape, если он не подходит под фильтр слоя
    if (layerFilter && shape.layer !== layerFilter) {
      return [shape];
    }
    
    if (shape.type === 'circle') {
      return trimCircleAgainstRects(shape, cylinders);
    }

    // Обрезаем только линии и polylines
    if (shape.type !== 'line' && shape.type !== 'polyline') {
      return [shape];
    }
    
    const trimmedSegments = trimLineAgainstRects(shape.points, cylinders);
    
    // Создаем новые shape для каждого обрезанного сегмента
    return trimmedSegments
      .filter(points => points && points.length >= 4)
      .map(points => ({
        ...shape,
        points,
      }));
  });
}
